import { reactive, computed } from 'vue'
import panelManager from '@/core/helpers/panelManager'

const state = reactive({
    isOpen: false,
    config: null,
    rows: [],
    columns: [],
    selectedIds: new Set(),
    expandedIds: new Set(),
    sortKey: null,
    sortDir: null,
    filterValues: {},
    page: 1,
    hasMore: false,
    loading: false,
    loadingMore: false,
    editingCell: null,
    columnWidths: {},
    sidebarOpen: false,
    columnVisibility: {},
    _unsortedRows: null,
})

function _fireCallback(name, ...args) {
    if (state.config && typeof state.config[name] === 'function') {
        return state.config[name](...args)
    }
}

function _getRowKey(row) {
    const key = state.config?.rowKey || 'id'
    return row[key]
}

function open(config) {
    const cols = (config.columns || []).map(c => ({
        key: c.key,
        label: c.label || c.key,
        width: c.width || null,
        flex: c.flex ?? (c.width ? undefined : 1),
        minWidth: c.minWidth || 40,
        sortable: c.sortable !== false,
        align: c.align || 'left',
        color: c.color || null,
        format: c.format || null,
        editable: c.editable || false,
        render: c.render || null,
        resizable: c.resizable !== false,
        visible: c.visible !== false,
        compactHide: c.compactHide || false,
        priority: c.priority || 'visible',
        cellClass: c.cellClass || '',
    }))

    const visibility = {}
    cols.forEach(c => { visibility[c.key] = c.visible })

    state.config = config
    state.columns = cols
    state.rows = config.rows || []
    state.selectedIds = new Set()
    state.expandedIds = new Set()
    state.sortKey = null
    state.sortDir = null
    state.filterValues = {}
    state.page = 1
    state.hasMore = config.totalCount ? config.rows.length < config.totalCount : false
    state.loading = false
    state.loadingMore = false
    state.editingCell = null
    state.columnWidths = {}
    state.sidebarOpen = config.filterLayout === 'sidebar'
    state.columnVisibility = visibility
    state.isOpen = true

    panelManager.openPanel('dataTablePanel')
}

function close() {
    _fireCallback('onClose')
    state.isOpen = false
    state.config = null
    state.rows = []
    state.columns = []
    state.selectedIds = new Set()
    state.expandedIds = new Set()
    panelManager.closePanel?.('dataTablePanel')
}

function setRows(rows) {
    state.rows = rows
    state.selectedIds = new Set()
    state._unsortedRows = null
    if (state.config?.totalCount) {
        state.hasMore = rows.length < state.config.totalCount
    }
    if (state.sortKey && state.sortDir) _sortRows()
}

function addRows(rows) {
    state.rows = [...state.rows, ...rows]
    if (state.config?.totalCount) {
        state.hasMore = state.rows.length < state.config.totalCount
    }
}

function updateRow(rowKeyValue, data) {
    const key = state.config?.rowKey || 'id'
    const idx = state.rows.findIndex(r => r[key] === rowKeyValue)
    if (idx !== -1) {
        state.rows[idx] = { ...state.rows[idx], ...data }
    }
}

function removeRow(rowKeyValue) {
    const key = state.config?.rowKey || 'id'
    state.rows = state.rows.filter(r => r[key] !== rowKeyValue)
    state.selectedIds.delete(rowKeyValue)
}

function setLoading(val) {
    state.loading = val
}

function setTitle(title) {
    if (state.config) state.config.title = title
}

function getSelectedRows() {
    const key = state.config?.rowKey || 'id'
    return state.rows.filter(r => state.selectedIds.has(r[key]))
}

function clearSelection() {
    state.selectedIds = new Set()
    _fireCallback('onSelectionChange', [])
}

function toggleSort(columnKey) {
    if (state.sortKey === columnKey) {
        if (state.sortDir === 'asc') state.sortDir = 'desc'
        else if (state.sortDir === 'desc') { state.sortKey = null; state.sortDir = null }
        else state.sortDir = 'asc'
    } else {
        state.sortKey = columnKey
        state.sortDir = 'asc'
    }
    const result = _fireCallback('onSort', state.sortKey, state.sortDir)
    if (result === undefined) _sortRows()
}

function _sortRows() {
    if (!state.sortKey || !state.sortDir) {
        if (state._unsortedRows) {
            state.rows = [...state._unsortedRows]
            state._unsortedRows = null
        }
        return
    }
    if (!state._unsortedRows) state._unsortedRows = [...state.rows]
    const key = state.sortKey
    const dir = state.sortDir === 'asc' ? 1 : -1
    state.rows = [...state.rows].sort((a, b) => {
        let va = a[key], vb = b[key]
        if (va == null && vb == null) return 0
        if (va == null) return dir
        if (vb == null) return -dir
        if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir
        va = String(va).toLocaleLowerCase('tr')
        vb = String(vb).toLocaleLowerCase('tr')
        return va.localeCompare(vb, 'tr') * dir
    })
}

function toggleSelect(rowKeyValue) {
    const next = new Set(state.selectedIds)
    if (next.has(rowKeyValue)) next.delete(rowKeyValue)
    else next.add(rowKeyValue)
    state.selectedIds = next
    _fireCallback('onSelectionChange', getSelectedRows())
}

function selectAll() {
    const key = state.config?.rowKey || 'id'
    const next = new Set()
    state.rows.forEach(r => {
        if (r._selectable !== false) next.add(r[key])
    })
    state.selectedIds = next
    _fireCallback('onSelectionChange', getSelectedRows())
}

function deselectAll() {
    state.selectedIds = new Set()
    _fireCallback('onSelectionChange', [])
}

function toggleExpand(rowKeyValue) {
    const next = new Set(state.expandedIds)
    if (next.has(rowKeyValue)) next.delete(rowKeyValue)
    else next.add(rowKeyValue)
    state.expandedIds = next
}

function startEdit(rowKey, columnKey) {
    state.editingCell = { rowKey, columnKey }
}

function cancelEdit() {
    state.editingCell = null
}

function commitEdit(rowKeyValue, columnKey, newValue) {
    const key = state.config?.rowKey || 'id'
    const row = state.rows.find(r => r[key] === rowKeyValue)
    if (!row) return
    const oldValue = row[columnKey]
    if (oldValue === newValue) { state.editingCell = null; return }
    row[columnKey] = newValue
    state.editingCell = null
    _fireCallback('onEdit', row, columnKey, newValue, oldValue)
}

async function loadMore() {
    if (state.loadingMore || !state.hasMore) return
    state.loadingMore = true
    state.page++
    try {
        await _fireCallback('onLoadMore', state.page, {
            sort: state.sortKey ? { key: state.sortKey, dir: state.sortDir } : null,
            filters: { ...state.filterValues },
        })
    } catch { /* empty */ }
    state.loadingMore = false
}

async function goToPage(page) {
    if (state.loading || page === state.page) return
    state.page = page
    state.rows = []
    state.loading = true
    try {
        await _fireCallback('onPageChange', page, {
            sort: state.sortKey ? { key: state.sortKey, dir: state.sortDir } : null,
            filters: { ...state.filterValues },
        })
    } catch { /* empty */ }
    state.loading = false
}

function setFilter(key, value) {
    state.filterValues = { ...state.filterValues, [key]: value }
    state.page = 1
    _fireCallback('onFilter', { ...state.filterValues })
}

function clearFilters() {
    state.filterValues = {}
    state.page = 1
    _fireCallback('onFilter', {})
}

function toggleColumnVisibility(columnKey) {
    state.columnVisibility = {
        ...state.columnVisibility,
        [columnKey]: !state.columnVisibility[columnKey],
    }
}

function resizeColumn(columnKey, width) {
    state.columnWidths = { ...state.columnWidths, [columnKey]: width }
}

function exportCsv() {
    const cols = state.columns.filter(c => state.columnVisibility[c.key] !== false)
    const rows = state.rows

    if (_fireCallback('onExport', rows, cols) !== undefined) return

    const header = cols.map(c => '"' + (c.label || c.key).replace(/"/g, '""') + '"').join(';')
    const body = rows.map(row =>
        cols.map(c => {
            let val = row[c.key]
            if (val == null) val = ''
            val = String(val).replace(/"/g, '""')
            return '"' + val + '"'
        }).join(';')
    ).join('\n')

    const title = state.config?.title || 'export'
    const date = new Date().toISOString().slice(0, 10)
    const filename = title.replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ _-]/g, '') + '_' + date + '.csv'

    const bom = '\uFEFF'
    const blob = new Blob([bom + header + '\n' + body], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
}

const visibleColumns = computed(() =>
    state.columns.filter(c => state.columnVisibility[c.key] !== false)
)

const allSelected = computed(() => {
    if (!state.rows.length) return false
    const key = state.config?.rowKey || 'id'
    return state.rows.every(r => r._selectable === false || state.selectedIds.has(r[key]))
})

const someSelected = computed(() => {
    return state.selectedIds.size > 0 && !allSelected.value
})

export default {
    state,
    open,
    close,
    setRows,
    addRows,
    updateRow,
    removeRow,
    setLoading,
    setTitle,
    getSelectedRows,
    clearSelection,
    toggleSort,
    toggleSelect,
    selectAll,
    deselectAll,
    toggleExpand,
    startEdit,
    cancelEdit,
    commitEdit,
    loadMore,
    goToPage,
    setFilter,
    clearFilters,
    toggleColumnVisibility,
    resizeColumn,
    exportCsv,
    visibleColumns,
    allSelected,
    someSelected,
    _fireCallback,
    _getRowKey,
}
